// ============================================================================
// DATA INTEGRITY FINDINGS HOOK
// ============================================================================
// Reads the open rows of public.data_integrity_findings so RADAR can surface
// them. The daily 02:15 job (check_stage_date_integrity) writes that table;
// nothing here recomputes anything.
//
// "Open" means neither cleared (the discrepancy went away) nor accepted
// (reviewed and deliberately left alone). Accepted findings reopen by
// themselves if their underlying dates change, so they do not need watching
// here.
//
// The table is admin-only under RLS, so a non-admin simply gets an empty list
// and the RADAR rule produces nothing — it fails closed rather than erroring.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export function useDataIntegrityFindings() {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFindings = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('data_integrity_findings')
        .select('check_name, subject, verdict, detail, first_seen_at, last_seen_at')
        .is('cleared_at', null)
        .is('accepted_at', null)
        .order('first_seen_at');

      if (err) throw err;

      setFindings((data || []).map(row => ({
        checkName:   row.check_name,
        subject:     row.subject,
        verdict:     row.verdict,
        detail:      row.detail || {},
        firstSeenAt: row.first_seen_at,
        lastSeenAt:  row.last_seen_at,
      })));
      setError(null);
    } catch (err) {
      // RLS denial for non-admins lands here; an empty list is the right result.
      console.error('[INTEGRITY] fetchFindings error:', err);
      setFindings([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFindings(); }, [fetchFindings]);

  return { findings, loading, error, refresh: fetchFindings };
}

export default useDataIntegrityFindings;
